/**
 * Zustand store for application form state management
 * Manages the state of the multi-step application modal and form
 *
 * Features:
 * - Persists candidate and CV data to localStorage
 * - Excludes UI state (modal open, current step, selected job) from persistence
 * - Step boundaries enforced (1-3)
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface ApplicationFormState {
  // Candidate data
  candidateEmail: string
  candidatePhone: string
  candidateFullName: string
  candidateLinkedIn: string | null

  // CV data
  cvFile: File | null
  cvFileKey: string | null
  cvFileName: string | null
  cvMimeType: string | null

  // UI state (not persisted)
  currentStep: 1 | 2 | 3
  isModalOpen: boolean
  selectedJobId: string | null
  recaptchaToken: string | null

  // Deprecated aliases for backwards compatibility (to be removed)
  /** @deprecated Use selectedJobId instead */
  selectedJobPostingId?: string | null
  /** @deprecated Use individual candidate fields instead */
  candidateInfo?: {
    fullName: string
    email: string
    phone: string
    linkedinUrl?: string
  }
  /** @deprecated Use individual CV fields instead */
  cvInfo?: {
    fileKey: string | null
    fileName: string | null
    mimeType: string | null
  }

  // Actions
  setCandidateInfo: (info: Partial<ApplicationFormState>) => void
  setCvInfo: (cv: Partial<ApplicationFormState>) => void
  nextStep: () => void
  prevStep: () => void
  setRecaptchaToken: (token: string) => void
  openModal: (jobId: string) => void
  closeModal: () => void
  reset: () => void
}

export const useApplicationFormStore = create<ApplicationFormState>()(
  persist(
    (set) => ({
      // Initial candidate data
      candidateEmail: '',
      candidatePhone: '',
      candidateFullName: '',
      candidateLinkedIn: null,

      // Initial CV data
      cvFile: null,
      cvFileKey: null,
      cvFileName: null,
      cvMimeType: null,

      // Initial UI state
      currentStep: 1,
      isModalOpen: false,
      selectedJobId: null,
      recaptchaToken: null,

      // Actions
      setCandidateInfo: (info) => set((state) => ({ ...state, ...info })),
      setCvInfo: (cv) => set((state) => ({ ...state, ...cv })),
      nextStep: () =>
        set((state) => ({
          currentStep: Math.min(3, state.currentStep + 1) as 1 | 2 | 3,
        })),
      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(1, state.currentStep - 1) as 1 | 2 | 3,
        })),
      setRecaptchaToken: (token: string) => set({ recaptchaToken: token }),
      openModal: (jobId: string) =>
        set({ isModalOpen: true, selectedJobId: jobId, currentStep: 1 }),
      closeModal: () =>
        set({ isModalOpen: false, currentStep: 1 }),
      reset: () =>
        set({
          candidateEmail: '',
          candidatePhone: '',
          candidateFullName: '',
          candidateLinkedIn: null,
          cvFile: null,
          cvFileKey: null,
          cvFileName: null,
          cvMimeType: null,
          currentStep: 1,
          isModalOpen: false,
          selectedJobId: null,
          recaptchaToken: null,
        }),
    }),
    {
      name: 'application-form-storage',
      // Only persist candidate and CV data, exclude UI state
      partialize: (state) => ({
        candidateEmail: state.candidateEmail,
        candidatePhone: state.candidatePhone,
        candidateFullName: state.candidateFullName,
        candidateLinkedIn: state.candidateLinkedIn,
        // Note: cvFile (File object) cannot be serialized to localStorage
        // Only persist the metadata
        cvFileKey: state.cvFileKey,
        cvFileName: state.cvFileName,
        cvMimeType: state.cvMimeType,
      }),
    }
  )
)
